// react
import { ReactNode, useCallback, useEffect, useRef } from 'react';

// utils
import dayjs from 'dayjs';
import { navigateCells } from './utils/navigateCells';
import { getShortCuts } from './utils/getShortCuts';
import { getSortedCalendars } from 'components/mrp/mrp.utils';
import { moveEventHighlight } from './utils/moveEventHighlight';
import { useTabThroughEvents } from './utils/tabThroughEvents.hooks';

// providers
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useCalendarNavigationStore } from '../store/calendarNavigationStore.store';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';

// constants
import { NavigationDirection } from './CalendarNavigationHandler.types';
import { IS_PREVIEW_ID } from 'components/mrp/components/AppointmentForm/AppointmentForm.consts';

const CalendarNavigationHandler = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  const {
    position,
    calendarEvents,
    event,
    setPosition,
    setEvent,
    removeCalendarEventsForCalendarIds
  } = useCalendarNavigationStore(
    useShallow((state) => ({
      position: state.position,
      calendarEvents: state.calendarEvents,
      event: state.event,
      setPosition: state.control.setPosition,
      setEvent: state.control.setEvent,
      removeCalendarEventsForCalendarIds:
        state.control.removeCalendarEventsForCalendarIds
    }))
  );

  const appointmentId = useAppointmentStore((state) => state.appointment?.id);

  // Remember which event was being edited so we can re-select it after the form closes
  const pendingRestoreEventId = useRef<string | number | undefined>(undefined);
  const pendingRestoreOriginalAppointmentId = useRef<
    string | number | undefined
  >(undefined);

  useEffect(() => {
    if (appointmentId && pendingRestoreEventId.current === undefined) {
      pendingRestoreEventId.current = appointmentId;
      pendingRestoreOriginalAppointmentId.current = calendarEvents[
        position?.calendarId || -1
      ]?.find((e) => e.id === appointmentId)?.planningEvent
        ?.originalAppointmentId;
    }
  }, [appointmentId, calendarEvents, position?.calendarId]);

  const { calendars, timeStep, timeSlotMin, timeSlotMax } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      timeStep: state.timeStep,
      timeSlotMin: state.timeSlotMin,
      timeSlotMax: state.timeSlotMax
    }))
  );

  const { startDate, endDate } = useSelectedDateRange();

  const { tabThroughEvents, isTabThroughActive: isCalendarNavigationActive } =
    useTabThroughEvents(getSortedCalendars(calendars), calendarEvents);

  // Update position to stay active when the date range no
  // longer contains the currentPosition.
  useEffect(() => {
    if (!position) {
      return;
    }

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    const currentWeekDay = position.date.isoWeekday();
    const startWeekDay = start.isoWeekday();

    const dateAdjustedPositionDate = !position.date.isBetween(start, end)
      ? start
          .add(currentWeekDay - startWeekDay, 'days')
          .set('hour', position.date.hour())
          .set('minutes', position.date.minute())
      : position.date;

    const minTime = dayjs(timeSlotMin);
    const maxTime = dayjs(timeSlotMax);

    const minTimeInMinutes = minTime.hour() * 60 + minTime.minute();
    const maxTimeInMinutes = maxTime.hour() * 60 + maxTime.minute();
    const positionDateTimeInMinutes =
      position.date.hour() * 60 + position.date.minute();

    const timeAdjustedPositionDate =
      minTimeInMinutes > positionDateTimeInMinutes
        ? // Position is outside of lower bounds, so we adjust it upwards
          dateAdjustedPositionDate
            .set('hour', minTime.hour())
            .set('minute', minTime.minute())
        : maxTimeInMinutes < positionDateTimeInMinutes
        ? // Position is outside of upper bounds, so we adjust it downwards
          dateAdjustedPositionDate
            .set('hour', maxTime.hour())
            .set('minute', maxTime.minute())
            .subtract(timeStep, 'minutes')
        : dateAdjustedPositionDate;

    if (!timeAdjustedPositionDate.isSame(position.date)) {
      setPosition({
        ...position,
        date: timeAdjustedPositionDate
      });
    }
  }, [startDate, endDate, timeSlotMin, timeSlotMax]);

  const resetPosition = useCallback(() => {
    if (Object.values(calendars).length === 0 || !startDate || !timeSlotMin) {
      setPosition(undefined);
      return;
    }

    if (!!position) {
      const calendarOnScreen = Object.values(calendars).some(
        ({ calendarId }) => calendarId === position?.calendarId
      );

      if (calendarOnScreen) return;
    }

    const calendarKeys = Object.keys(calendars).map((key) => Number(key));
    const newestKey = Math.max(...calendarKeys);
    const targetCalendar = calendars[newestKey];

    const start = dayjs(startDate);
    const minTime = dayjs(timeSlotMin);
    const firstDateTime = start
      .set('hour', minTime.hour())
      .set('minute', minTime.minute());

    setPosition({
      calendarId: targetCalendar.calendarId,
      date: firstDateTime
    });
  }, [calendars, startDate, timeSlotMin, position]);

  useEffect(() => {
    if (!position) {
      return;
    }

    // Ensure the currentCalendar state stays in line with what is visible
    // on screen. If a calendar is closed, we lose the position and clear
    // the currentCalendar.
    if (!calendars[position.calendarId]) {
      resetPosition();
      setEvent(undefined);
    }

    // If a calendar was close for which calendarEvents are being tracked,
    // we can dispose of those events.
    const openedCalendarIds = Object.keys(calendars);
    const dataCalendarIds = Object.keys(calendarEvents);

    const closedCalendarIds = dataCalendarIds.filter(
      (id) => !openedCalendarIds.includes(id)
    );

    removeCalendarEventsForCalendarIds(closedCalendarIds);
  }, [calendars]);

  useEffect(() => {
    resetPosition();
  }, [calendars, startDate, timeSlotMin, position]);

  // When timestep update, we need to round the selected position
  // to match the cell that would contain the old position.
  useEffect(() => {
    if (!position) return;

    moveEventHighlight({
      pendingRestoreEventId: pendingRestoreEventId.current,
      calendarEvents,
      position,
      timeStep,
      event,
      setEvent
    });

    const totalMinutes = position.date.hour() * 60 + position.date.minute();
    const flooredMinutes = Math.floor(totalMinutes / timeStep) * timeStep;

    if (totalMinutes !== flooredMinutes) {
      const newDate = position.date
        .startOf('day')
        .add(flooredMinutes, 'minute');

      setPosition({
        ...position,
        date: newDate
      });
    }
  }, [calendarEvents, event, position, setEvent, setPosition, timeStep]);

  useEffect(() => {
    if (!position) return;

    const currentCalendarEvents =
      calendarEvents[position.calendarId || -1] || [];

    if (event) {
      // Deselect event when no longer available in the active position
      if (currentCalendarEvents.indexOf(event) === -1) {
        // After a refetch, event object references change — match by id to preserve selection
        const matchedEvent = currentCalendarEvents.find(
          (e) =>
            e.id === event.id ||
            e.planningEvent?.originalAppointmentId ===
              event.planningEvent?.originalAppointmentId
        );
        if (!currentCalendarEvents.find((e) => e.id === IS_PREVIEW_ID)) {
          setEvent(matchedEvent);
        }
      }
    } else if (
      pendingRestoreEventId.current !== undefined &&
      !currentCalendarEvents.find((e) => e.id === IS_PREVIEW_ID)
    ) {
      // Form just closed — restore the previously selected event by id
      const idToRestore = pendingRestoreEventId.current;
      const matchedEvent = currentCalendarEvents.find(
        (e) =>
          String(e.id) === String(idToRestore) ||
          String(e.planningEvent?.originalAppointmentId) ===
            String(pendingRestoreOriginalAppointmentId.current)
      );
      if (matchedEvent) {
        pendingRestoreEventId.current = undefined;
        pendingRestoreOriginalAppointmentId.current = undefined;

        setEvent(matchedEvent);
      }
    }
  }, [calendarEvents, event, position, setEvent]);

  const tabThroughEventsCallback = useCallback(
    (backwards = false) => {
      tabThroughEvents({
        backwards,
        event,
        position,
        setEvent,
        setPosition
      });
    },
    [event, position, setEvent, setPosition, tabThroughEvents]
  );

  const navigate = (direction: NavigationDirection) => {
    const newPosition = navigateCells({
      direction,
      calendars: getSortedCalendars(calendars),
      endDate,
      position,
      event,
      startDate,
      timeStep,
      timeSlotMin,
      timeSlotMax
    });

    if (!newPosition) return;

    setPosition(newPosition);
  };

  useCreateShortcutContext(
    'calendar-navigation',
    getShortCuts({
      navigate,
      tabThroughEvents: tabThroughEventsCallback,
      active: isCalendarNavigationActive
    }),
    [position, event, calendarEvents, calendars]
  );

  return <>{children}</>;
};

export default CalendarNavigationHandler;
